import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { ThemeToggleDesktop, ThemeToggleMobile } from './ThemeToggleButton';
import { useTheme } from '../../contexts/ThemeContext';
import { useBooking } from '../../contexts/BookingContext';
import { useGoogleAnalytics } from '../../hooks/useGoogleAnalytics';

/**
 * OptimizedHeader - Consolidated header component with best features from all variants
 * 
 * Key improvements:
 * - Simplified navigation structure (3 main categories)
 * - Enhanced mobile experience with smooth animations
 * - Better accessibility with proper ARIA labels
 * - Optimized performance with reduced re-renders
 * - Consistent CTA hierarchy
 * - Improved keyboard navigation
 */
const OptimizedHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme, toggleLightDark, isDark } = useTheme();
  const { openBooking } = useBooking();
  const { trackEvent } = useGoogleAnalytics();
  
  const dropdownTimeoutRef = useRef(null);
  const menuButtonRef = useRef(null);
  const dropdownRefs = useRef([]);

  // OPTIMIZED NAVIGATION - 3 main categories for better UX
  const navigationItems = [
    {
      name: 'GCC Setup',
      icon: 'Rocket',
      isDropdown: true,
      ariaLabel: 'Launch your India GCC with speed and certainty',
      items: [
        {
          name: 'Overview',
          path: '/services',
          description: 'C-suite focused: 90-day guarantee, transparent ROI, zero risk',
          ariaLabel: 'View GCC Setup overview',
          icon: 'Award',
          featured: true
        },
        {
          name: 'Launch Blueprint',
          path: '/services#launch-blueprint',
          description: '90-day launch methodology and milestone governance',
          ariaLabel: 'See GCC launch blueprint',
          icon: 'Flag'
        },
        {
          name: 'AI Intelligence',
          path: '/services#ai-intelligence',
          description: 'Bootminds AI-powered GCC setup and optimization',
          ariaLabel: 'View AI powered GCC solutions',
          icon: 'Sparkles'
        },
        {
          name: 'ROI Calculator',
          path: '/services#roi-calculator',
          description: 'Interactive ROI calculators and decision tools',
          ariaLabel: 'Explore ROI calculators',
          icon: 'Calculator'
        },
        {
          name: 'Pricing & Economics',
          path: '/services#pricing',
          description: 'Transparent pricing and cost breakdown',
          ariaLabel: 'View pricing and economics',
          icon: 'DollarSign'
        },
        {
          name: 'Methodology',
          path: '/services#methodology',
          description: 'Our proven 90-day implementation process',
          ariaLabel: 'View methodology',
          icon: 'Layers'
        },
        {
          name: 'Why Bootminds',
          path: '/services#differentiation',
          description: 'What makes us different from alternatives',
          ariaLabel: 'See why choose Bootminds',
          icon: 'Award'
        }
      ]
    },
    {
      name: 'GCC Expansion',
      icon: 'Globe2',
      isDropdown: true,
      ariaLabel: 'Plan multi-city and international GCC scale',
      items: [
        {
          name: 'Expansion Overview',
          path: '/services/expansion',
          description: 'Complete expansion services hub',
          ariaLabel: 'View GCC expansion services',
          icon: 'Globe2',
          featured: true
        },
        {
          name: 'Expansion Playbooks',
          path: '/services/expansion#expansion-playbooks',
          description: 'Multi-city roadmap, governance and operating playbooks',
          ariaLabel: 'Explore expansion playbooks',
          icon: 'Map'
        },
        {
          name: 'Government Partnerships',
          path: '/services/expansion#government-partnerships',
          description: 'Incentive negotiation and state/EODB access',
          ariaLabel: 'See government partnership capabilities',
          icon: 'Building2'
        },
        {
          name: 'Market Intelligence',
          path: '/services/expansion#market-intelligence',
          description: 'Real-time signals to choose your next location',
          ariaLabel: 'Open market intelligence hub',
          icon: 'TrendingUp'
        }
      ]
    },
    {
      name: 'Executive Hiring',
      icon: 'UserCheck',
      isDropdown: true,
      ariaLabel: 'Leadership hiring and executive search services',
      items: [
        {
          name: 'Leadership Search',
          path: '/services/executive-hiring',
          description: 'Director-to-CXO search with 25-day promise',
          ariaLabel: 'View leadership search services',
          icon: 'Briefcase'
        },
        {
          name: 'Hiring Process',
          path: '/services/executive-hiring#leadership',
          description: '4-phase executive search methodology',
          ariaLabel: 'View hiring process',
          icon: 'Target'
        },
        {
          name: 'Industry & Functional Expertise',
          path: '/services/executive-hiring#expertise',
          description: 'Technology, operations, finance, and HR leadership',
          ariaLabel: 'View industry and functional expertise',
          icon: 'Award'
        }
      ]
    },
    {
      name: 'SkillStack Staffing',
      icon: 'Layers',
      isDropdown: true,
      ariaLabel: 'Staffing models powered by SkillStack intelligence',
      items: [
        {
          name: 'Staffing Models',
          path: '/services/staffing#staffing-models',
          description: 'Contract, C2H, FTE comparison with 25-day SLA',
          ariaLabel: 'Compare staffing models',
          icon: 'Table'
        },
        {
          name: 'Expertise Areas',
          path: '/services/staffing#expertise-areas',
          description: 'Technology, operations, finance, and HR talent pools',
          ariaLabel: 'View expertise areas',
          icon: 'Award'
        },
        {
          name: 'Candidate Experience',
          path: '/services/staffing#candidate-experience',
          description: 'Premium transportation, analytics, and engagement',
          ariaLabel: 'See candidate experience engine',
          icon: 'HeartPulse'
        },
        {
          name: 'Retention & Guarantees',
          path: '/services/staffing#retention',
          description: '15-day replacements, conversion analytics, compliance',
          ariaLabel: 'Review retention and guarantee policies',
          icon: 'ShieldCheck'
        },
        {
          name: 'TA Strategy Report',
          path: '/services/staffing#ta-strategy',
          description: 'Workshop-driven TA blueprint ($10K fixed fee)',
          ariaLabel: 'Learn about TA strategy report',
          icon: 'ClipboardList'
        },
        {
          name: 'Workshop Methodology',
          path: '/services/staffing#workshop',
          description: 'Stakeholder interviews and AI impact assessment',
          ariaLabel: 'Review workshop methodology',
          icon: 'Users2'
        }
      ]
    },
    {
      name: 'About Us',
      icon: 'Users',
      isDropdown: true,
      ariaLabel: 'Company overview, team, and success stories',
      items: [
        {
          name: 'Overview',
          path: '/about',
          description: 'Company overview, mission, and values',
          ariaLabel: 'View company overview',
          icon: 'BookOpen'
        },
        {
          name: 'Success Stories',
          path: '/case-studies',
          description: 'Client transformations and case studies',
          ariaLabel: 'View success stories',
          icon: 'Award'
        },
        {
          name: 'Career',
          path: 'https://career.bootminds.com',
          description: 'Join our team and build the future of GCCs',
          ariaLabel: 'View career opportunities at Bootminds',
          icon: 'Briefcase',
          isExternal: true
        }
      ]
    }
  ];

  // Enhanced scroll and interaction handling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('header')) {
        closeMenu();
      }
      if (activeDropdown !== null) {
        const dropdownElement = document.querySelector(`[data-dropdown="${activeDropdown}"]`);
        const buttonElement = dropdownRefs.current[activeDropdown];
        
        if (dropdownElement && !dropdownElement.contains(event.target) && 
            buttonElement && !buttonElement.contains(event.target)) {
          setActiveDropdown(null);
        }
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (activeDropdown !== null) {
          setActiveDropdown(null);
          const dropdownButton = dropdownRefs.current[activeDropdown];
          if (dropdownButton) {
            dropdownButton.focus();
          }
        } else if (isMenuOpen) {
          closeMenu();
          if (menuButtonRef.current) {
            menuButtonRef.current.focus();
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [isMenuOpen, activeDropdown]);

  // Optimized menu toggle with better state management
  const toggleMenu = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    setActiveDropdown(null);
    
    trackEvent('navigation_menu_toggle', {
      action: newMenuState ? 'open' : 'close',
      page: location.pathname
    });
    
    if (newMenuState) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
    document.body.classList.remove('overflow-hidden');
  };

  // Enhanced dropdown handling - click navigates to first item
  const handleDropdownClick = (index) => {
    const item = navigationItems[index];
    if (item.items && item.items.length > 0) {
      // Navigate to the first (featured) item's path
      const firstItem = item.items[0];
      handleNavigationClick(item.name, firstItem.path);
    }
  };

  // Toggle dropdown (for keyboard/accessibility)
  const handleDropdownToggle = (index) => {
    const newActiveDropdown = activeDropdown === index ? null : index;
    setActiveDropdown(newActiveDropdown);
    
    trackEvent('dropdown_toggle', {
      dropdown: navigationItems[index].name,
      action: newActiveDropdown !== null ? 'open' : 'close',
      page: location.pathname
    });
    
    if (newActiveDropdown !== null) {
      setTimeout(() => {
        const firstItem = document.querySelector(`[data-dropdown="${index}"] a`);
        if (firstItem) {
          firstItem.focus();
        }
      }, 100);
    }
  };

  // Smooth hover behavior for desktop
  const handleDropdownMouseEnter = (index) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(index);
  };

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  // Enhanced keyboard navigation
  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDropdownToggle(index);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (activeDropdown !== index) {
        setActiveDropdown(index);
      }
      setTimeout(() => {
        const firstItem = document.querySelector(`[data-dropdown="${index}"] a`);
        if (firstItem) {
          firstItem.focus();
        }
      }, 100);
    } else if (event.key === 'Escape') {
      setActiveDropdown(null);
    }
  };

  const handleDropdownItemKeyDown = (event, currentIndex, itemIndex) => {
    const items = document.querySelectorAll(`[data-dropdown="${currentIndex}"] a`);
    
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = (itemIndex + 1) % items.length;
      items[nextIndex]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevIndex = itemIndex === 0 ? items.length - 1 : itemIndex - 1;
      items[prevIndex]?.focus();
    } else if (event.key === 'Tab' && !event.shiftKey && itemIndex === items.length - 1) {
      setActiveDropdown(null);
    }
  };

  // Optimized navigation click handler
  const handleNavigationClick = (itemName, path) => {
    trackEvent('navigation_click', {
      item_name: itemName,
      destination: path,
      page: location.pathname
    });
    closeMenu();
    
    setTimeout(() => {
      // Handle anchor links properly
      if (path.includes('#')) {
        const [pathname, hash] = path.split('#');
        
        // Check if we're already on this page
        const isCurrentPage = location.pathname === pathname || 
                              (pathname === '' && location.pathname === '/');
        
        if (isCurrentPage) {
          // Already on the page, just scroll to anchor
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
        } else {
          // Navigate to the page first
          navigate(pathname);
          
          // Wait for page to load, then scroll to anchor
          setTimeout(() => {
            const element = document.getElementById(hash);
            if (element) {
              element.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
              });
            }
          }, 300);
        }
      } else {
        navigate(path);
      }
    }, 150);
  };

  // Path matching utilities
  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const isActiveSection = (item) => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.items) {
      return item.items.some(subItem => location.pathname === subItem.path || 
        (subItem.path.includes('#') && location.pathname === subItem.path.split('#')[0]));
    }
    return false;
  };

  // CTA click handler
  const handleCTAClick = (source) => {
    trackEvent('cta_click', {
      cta_name: 'Book a Strategy Call',
      location: source,
      page: location.pathname
    });
    openBooking(source);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-md shadow-lg border-b border-border' 
          : 'bg-background/90 backdrop-blur-sm'
      }`}
      role="banner"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo - Enhanced with better focus states */}
          <Link 
            to="/" 
            className="flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md transition-all duration-200 hover:opacity-80"
            onClick={() => handleNavigationClick('Logo', '/')}
            aria-label="Bootminds Elite - Return to homepage"
          >
            <div className="relative">
              <picture>
                <source
                  srcSet={isDark ? "/assets/images/White_logo_Bootminds.webp" : "/assets/images/Black_logo_Bootminds.webp"}
                  type="image/webp"
                />
                <img
                  src={isDark ? "/assets/images/White_logo_Bootminds.png" : "/assets/images/Black_logo_Bootminds.png"}
                  alt="Bootminds Elite - Global Capability Center consulting company logo"
                  width="200"
                  height="36"
                  className="h-8 w-auto lg:h-10 transition-all duration-200"
                  loading="eager"
                  decoding="async"
                />
              </picture>
            </div>
          </Link>

          {/* Desktop Navigation - Optimized with icons */}
          <nav className="hidden lg:flex items-center space-x-1" role="navigation" aria-label="Main navigation">
            {navigationItems.map((item, index) => (
              <div key={index} className="relative">
                {item.isDropdown ? (
                  <div>
                    <button
                      ref={el => dropdownRefs.current[index] = el}
                      className={`flex items-center space-x-2 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 hover:bg-muted/20 ${
                        isActiveSection(item)
                          ? 'bg-primary/10 text-primary' : 'text-text-primary'
                      }`}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onClick={() => handleDropdownClick(index)}
                      onMouseEnter={() => handleDropdownMouseEnter(index)}
                      onMouseLeave={handleDropdownMouseLeave}
                      aria-expanded={activeDropdown === index}
                      aria-haspopup="true"
                      aria-label={item.ariaLabel}
                    >
                      <Icon 
                        name={item.icon} 
                        size={16} 
                        color="currentColor"
                        aria-hidden="true"
                      />
                      <span className="font-medium text-sm">
                        {item.name}
                      </span>
                      <Icon 
                        name="ChevronDown" 
                        size={14} 
                        color="currentColor"
                        aria-hidden="true"
                        className={`transition-transform duration-200 ${
                          activeDropdown === index ? 'rotate-180' : 'rotate-0'
                        }`}
                      />
                    </button>
                    
                    {/* Enhanced Dropdown Menu */}
                    <div 
                      data-dropdown={index}
                      className={`absolute top-full left-0 mt-2 w-80 bg-background border border-border rounded-xl shadow-xl transition-all duration-300 ease-out ${
                        activeDropdown === index 
                          ? 'opacity-100 visible translate-y-0 scale-100' 
                          : 'opacity-0 invisible translate-y-2 scale-95'
                      }`}
                      onMouseEnter={() => handleDropdownMouseEnter(index)}
                      onMouseLeave={handleDropdownMouseLeave}
                      role="menu"
                      aria-label={`${item.name} submenu`}
                      style={{ 
                        position: 'absolute', 
                        zIndex: 10000,
                        transformOrigin: 'top center'
                      }}
                    >
                      <div className="py-2">
                        {item.items.map((subItem, itemIndex) => {
                          const linkClassName = `flex items-center space-x-3 px-4 py-3 focus:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset transition-all duration-200 hover:bg-muted/30 hover:translate-x-1 ${
                            subItem.featured 
                              ? 'bg-primary/10 border border-primary/20 rounded-lg mb-2 hover:bg-primary/15' 
                              : ''
                          } ${
                            isActivePath(subItem.path)
                              ? 'bg-primary/5 text-primary border-r-2 border-primary' 
                              : 'text-text-primary'
                          }`;
                          
                          const linkContent = (
                            <>
                              <Icon 
                                name={subItem.icon} 
                                size={16} 
                                color="currentColor"
                                aria-hidden="true"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-sm mb-1 flex items-center space-x-2">
                                  <span>{subItem.name}</span>
                                  {subItem.featured && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-accent text-accent-foreground">
                                      NEW
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-text-secondary">
                                  {subItem.description}
                                </div>
                              </div>
                            </>
                          );

                          return subItem.isExternal ? (
                            <a
                              key={subItem.path}
                              href={subItem.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={linkClassName}
                              onKeyDown={(e) => handleDropdownItemKeyDown(e, index, itemIndex)}
                              role="menuitem"
                              aria-label={subItem.ariaLabel}
                              onClick={() => setActiveDropdown(null)}
                            >
                              {linkContent}
                            </a>
                          ) : (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={linkClassName}
                              onClick={() => {
                                handleNavigationClick(subItem.name, subItem.path);
                                setActiveDropdown(null);
                              }}
                              onKeyDown={(e) => handleDropdownItemKeyDown(e, index, itemIndex)}
                              role="menuitem"
                              aria-label={subItem.ariaLabel}
                            >
                              {linkContent}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 hover:bg-muted/20 ${
                      isActivePath(item.path)
                        ? 'bg-primary/10 text-primary' : 'text-text-primary'
                    }`}
                    onClick={() => handleNavigationClick(item.name, item.path)}
                    aria-label={item.ariaLabel}
                  >
                    <Icon 
                      name={item.icon} 
                      size={16} 
                      color="currentColor"
                      aria-hidden="true"
                    />
                    <span className="font-medium text-sm">
                      {item.name}
                    </span>
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Right Side Actions - Optimized CTA hierarchy */}
          <div className="flex items-center space-x-3">
            {/* Theme Toggle Button - Desktop */}
<ThemeToggleDesktop />

            {/* Primary CTA - Desktop */}
            <Button
              variant="default"
              size="lg"
              className="hidden md:flex bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:ring-offset-2 px-6 min-w-[200px] transition-all duration-200"
              iconName="Calendar"
              iconPosition="left"
              iconSize={18}
              onClick={() => handleCTAClick('header')}
            >
              Book a Strategy Call
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              ref={menuButtonRef}
              onClick={toggleMenu}
              className="lg:hidden p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 hover:bg-muted/20 text-text-primary"
              aria-label={`${isMenuOpen ? 'Close' : 'Open'} navigation menu`}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              style={{ minHeight: '44px', minWidth: '44px' }}
            >
              <Icon 
                name={isMenuOpen ? "X" : "Menu"} 
                size={24} 
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        {/* Enhanced Mobile Navigation Menu */}
        <div 
          id="mobile-menu"
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ 
            position: 'fixed', 
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 999,
            backgroundColor: 'var(--color-background)',
            borderTop: '1px solid var(--color-border)',
            maxHeight: isMenuOpen ? 'calc(100vh - 100%)' : '0',
          }}
          aria-hidden={!isMenuOpen}
        >
          <nav 
            className="py-4 border-t border-border"
            role="navigation" 
            aria-label="Mobile navigation"
          >
            <div className="space-y-2">
              {/* Theme Toggle for Mobile */}
              <div className="px-4 py-3 border-b border-border mb-4">
                <ThemeToggleMobile />
              </div>

              {/* Mobile Navigation Items */}
              {navigationItems.map((item, index) => (
                <div key={index}>
                  {item.isDropdown ? (
                    <div>
                      <button
                        onClick={() => handleDropdownToggle(index)}
                        className={`w-full flex items-center justify-between p-4 rounded-lg text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset ${
                          isActiveSection(item)
                            ? 'bg-primary/10 text-primary' : 'text-text-primary hover:bg-muted/20'
                        }`}
                        aria-expanded={activeDropdown === index}
                        aria-haspopup="true"
                        aria-label={item.ariaLabel}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon 
                            name={item.icon} 
                            size={20} 
                            color="currentColor"
                            aria-hidden="true"
                          />
                          <span className="font-medium">{item.name}</span>
                        </div>
                        <Icon 
                          name="ChevronDown" 
                          size={16} 
                          color="currentColor"
                          aria-hidden="true"
                          className={`transition-transform duration-200 ${
                            activeDropdown === index ? 'rotate-180' : 'rotate-0'
                          }`}
                        />
                      </button>
                      
                      {/* Mobile Dropdown Items */}
                      {activeDropdown === index && (
                        <div className="ml-6 mt-2 space-y-1">
                          {item.items.map((subItem, subIndex) => {
                            const mobileLinkClassName = `flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset ${
                              subItem.featured 
                                ? 'bg-primary/10 border border-primary/20 hover:bg-primary/15' 
                                : ''
                            } ${
                              isActivePath(subItem.path)
                                ? 'bg-primary/5 text-primary' : 'text-text-secondary hover:bg-muted/20'
                            }`;
                            
                            const mobileLinkContent = (
                              <>
                                <Icon 
                                  name={subItem.icon} 
                                  size={16} 
                                  color="currentColor"
                                  aria-hidden="true"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-sm flex items-center space-x-2">
                                    <span>{subItem.name}</span>
                                    {subItem.featured && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-accent text-accent-foreground">
                                        NEW
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs opacity-75">{subItem.description}</div>
                                </div>
                              </>
                            );

                            return subItem.isExternal ? (
                              <a
                                key={subItem.path}
                                href={subItem.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={mobileLinkClassName}
                                aria-label={subItem.ariaLabel}
                                onClick={() => {
                                  setIsMenuOpen(false);
                                  setActiveDropdown(null);
                                }}
                              >
                                {mobileLinkContent}
                              </a>
                            ) : (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                className={mobileLinkClassName}
                                onClick={() => {
                                  handleNavigationClick(subItem.name, subItem.path);
                                  setIsMenuOpen(false);
                                  setActiveDropdown(null);
                                }}
                                aria-label={subItem.ariaLabel}
                              >
                                {mobileLinkContent}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`flex items-center space-x-3 p-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset ${
                        isActivePath(item.path)
                          ? 'bg-primary/10 text-primary' : 'text-text-primary hover:bg-muted/20'
                      }`}
                      onClick={() => {
                        handleNavigationClick(item.name, item.path);
                        setIsMenuOpen(false);
                      }}
                      aria-label={item.ariaLabel}
                    >
                      <Icon 
                        name={item.icon} 
                        size={20} 
                        color="currentColor"
                        aria-hidden="true"
                      />
                      <span className="font-medium">{item.name}</span>
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile CTA */}
              <div className="pt-4 border-t border-border">
                <Button
                  variant="default"
                  size="lg"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200"
                  iconName="Calendar"
                  iconPosition="left"
                  iconSize={18}
                  onClick={() => {
                    handleCTAClick('mobile_menu');
                    setIsMenuOpen(false);
                  }}
                >
                  Book a Strategy Call
                </Button>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default OptimizedHeader;
